// Edit layer style editor (edits list of rules)

import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, IconButton, Stack, Typography } from "@mui/material";
import { theme_limeGreen, theme_orange, theme_textColorBlended, theme_textColorMain } from "../../../Theme";
import { ILayer } from '../../../Layers/LayerInterfaces';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ChangeEvent, useState } from "react";
import { EditLayerStyleRule, GetDefaultLayerStyle_DefaultRule, GetDefaultLayerStyle_RegularRule as GetDefaultLayerStyle_RegularRule } from "./EditLayerStyleRule";
import { CustomTextField } from "../EditLayer";
import YesNoDialog from "../../../Components/YesNoDialog";
import { ILayerStyle, ILayerStyleRule, TLayerStyleShapeType } from "./LayerStyleInterfaces";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { ToastNotification } from "../../../ToastNotifications";
import { OnExpandRule_UniqueValues } from "./EditLayerStyle_ColorScheme_UniqueValues";
import { OnExpandRule_Classified } from "./EditLayerStyle_ColorScheme_Classified";


export const LAYER_STYLE_EDITOR_SPECIAL_DEFAULT_RULE_ID = 0;


//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface EditLayerStyleEditorProps
{
  layer?: ILayer;
  layer_shape_type: TLayerStyleShapeType;
  style: ILayerStyle;   // input
  OnStyleUpdated: any;  // output
}

//-------------------------------------------------------------------------------
// Edit layer style editor (edits list of rules)
//-------------------------------------------------------------------------------
export function EditLayerStyleEditor(props: EditLayerStyleEditorProps) 
{


  const [deleteRuleConfirmation, setDeleteRuleConfirmation] = useState<boolean>(false);
  const [deleteRule, setDeleteRule] = useState<ILayerStyleRule|undefined>(undefined);





  //-------------------------------------------------------------------------------
  // A rule's on/off checkbox was changed.
  // 
  // NOTE: We use onClick instead of onChange because onClick allows us to stop
  //       propagation of the mouse click event (otherwise it would trigger the accordion).  
  //-------------------------------------------------------------------------------
  const OnRuleCheckBoxClicked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, rule: ILayerStyleRule) => 
  {
    if(!props.style || !rule) return;

    // Prevent click-through (which would trigger the accordion open/close)
    event.preventDefault();
    event.stopPropagation();

    const updatedRule: ILayerStyleRule =
    {
      ...rule,
      enabled: !rule.enabled
    }

    OnRuleUpdated(updatedRule);
  }

  //-------------------------------------------------------------------------------
  // A rule's delete buttons was clicked.
  //-------------------------------------------------------------------------------
  const OnConfirmDeleteRule = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, rule: ILayerStyleRule) => 
  {
    // Prevent click-through (which would trigger the accordion open/close)
    event.preventDefault();
    event.stopPropagation();

    setDeleteRule(rule);
    setDeleteRuleConfirmation(true);
  }
  
  //-------------------------------------------------------------------------------
  // A rule's accordion was expanded or collapsed.
  //-------------------------------------------------------------------------------
  const OnAccordionExpandChange = (event: React.SyntheticEvent<Element, Event>, expanded: boolean, clickedRule: ILayerStyleRule) => 
  {
    if(!props.style) return;

    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      active_rule_id: expanded === true ? clickedRule.id : undefined
    }

    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // The style's name has changed.
  //-------------------------------------------------------------------------------
  function OnStyleNameChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.style) return;

    const newValueStr: string = event.target.value;

    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      name: newValueStr
    }

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // The style's description has changed.
  //-------------------------------------------------------------------------------
  function OnStyleDescriptionChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void 
  {
    if(!props.style) return;

    const newValueStr: string = event.target.value;

    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      description: newValueStr
    }

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // 
  //-------------------------------------------------------------------------------  
  function OnCreateNewRule()
  {
    if(!props.style) return;

    const newRule: ILayerStyleRule = GetDefaultLayerStyle_RegularRule(props.style.rules, props.layer_shape_type);

    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      rules: [...props.style.rules, newRule],
      active_rule_id: newRule.id
    }

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // 
  //-------------------------------------------------------------------------------  
  function OnDeleteRule()
  {
    setDeleteRuleConfirmation(false);

    if(!props.style || !deleteRule) return;

    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      rules: props.style.rules.filter(r => r.id !== deleteRule.id)
    }

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // A Rule has been updated in the rule editor.
  //-------------------------------------------------------------------------------  
  function OnRuleUpdated(updatedRule: ILayerStyleRule)
  {
    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      rules: props.style.rules.map(oldRule => oldRule.id === updatedRule.id ? updatedRule : oldRule)
    }    

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // The special "Default" Rule has been updated in the rule editor.
  //-------------------------------------------------------------------------------  
  function OnDefaultSpecialRuleUpdated(updatedRule: ILayerStyleRule)
  {
    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      default_rule: updatedRule
    }    

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // A rule's MOVE UP button was clicked.
  //-------------------------------------------------------------------------------
  const OnMoveRuleUp = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, rule_index: number) => 
  {
    // Prevent click-through (which would trigger the accordion open/close)
    event.preventDefault();
    event.stopPropagation();

    // Ignore if this is the top-most item
    if(rule_index === 0)
      return;

    // Create a new rules array, and move the rule at the specified index UP by one
    const new_rules_array = [...props.style.rules];
    [new_rules_array[rule_index - 1], new_rules_array[rule_index]] = [new_rules_array[rule_index], new_rules_array[rule_index - 1]];

    // Create an updated style
    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      rules: new_rules_array
    }

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // A rule's MOVE DOWN button was clicked.
  //-------------------------------------------------------------------------------
  const OnMoveRuleDown = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, rule_index: number) => 
  {
    // Prevent click-through (which would trigger the accordion open/close)
    event.preventDefault();
    event.stopPropagation();

    // Ignore if this is the bottom-most item

    if (rule_index >= props.style.rules.length - 1 || rule_index < 0)
      return;

    // Create a new rules array, and move the rule at the specified index DOWN by one
    const new_rules_array = [...props.style.rules];
    [new_rules_array[rule_index], new_rules_array[rule_index + 1]] = [new_rules_array[rule_index + 1], new_rules_array[rule_index]];

    // Create an updated style
    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      rules: new_rules_array
    }

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);
  }

  //-------------------------------------------------------------------------------
  // Toggles "advanced" mode ON/OFF
  //-------------------------------------------------------------------------------
  function OnToggleAdvancedMode()
  {
    // Create an updated style
    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      show_advanced_mode: props.style.show_advanced_mode === true ? false : true
    }    

    // Notify caller of the change
    props.OnStyleUpdated(updatedStyle);    
  }
  
  //-------------------------------------------------------------------------------
  // A rule's accordion was expanded or collapsed.
  //-------------------------------------------------------------------------------
  function OnExpandRule(rule: ILayerStyleRule)
  {
    if(!props.style || !rule || !rule.geometry) return;

    let expanded_rules: ILayerStyleRule[] | undefined = undefined;

    let next_rule_id: number = props.style.rules.reduce((prev, current) => (prev.id > current.id) ? prev : current).id + 1;

    switch(rule.geometry.color_scheme)
    {
      case 'unique values': 
        expanded_rules = OnExpandRule_UniqueValues(rule, next_rule_id);
        break;
      case 'classified':
        expanded_rules = OnExpandRule_Classified(rule, next_rule_id);
        break;
      default: 
        ToastNotification('error','Rule expansion is not supported for this color scheme')
        return;
    }

    if(!expanded_rules || expanded_rules.length === 0)
      return;

    // Turn OFF the original rule, but leave it where it is
    //
    // NOTE: We could delete it, but it's easy enough for the user to do it.  If 
    //       they change their mind, not deleting the original rule allows them to 
    //       "undo" the expansion.

    const updatedRule: ILayerStyleRule = 
    {
      ...rule,
      enabled: false,
      
    }

    let updatedStyleRules: ILayerStyleRule[] = props.style.rules.map(oldRule => oldRule.id === updatedRule.id ? updatedRule : oldRule);
    
    // Add in all the new expanded rules (immediately following the original rule)

    const origRuleIndex: number = props.style.rules.findIndex(r => r.id === rule.id);

    updatedStyleRules = 
    [
      ...updatedStyleRules.slice(0, origRuleIndex+1),
      ...expanded_rules,
      ...updatedStyleRules.slice(origRuleIndex+1),
    ];

    // Create an updated style using the updated rules array

    const updatedStyle: ILayerStyle =
    {
      ...props.style,
      rules: updatedStyleRules,
      active_rule_id: undefined,
    }

    // Update the style
    props.OnStyleUpdated(updatedStyle);
  }








  

  // Main render

  return (

    <Stack direction='column'>

      {/* Style name */}

      <CustomTextField variant='standard' size='small' autoComplete='off'
                       value={props.style.name}
                       onChange={OnStyleNameChanged}
                       label={<Typography sx={{fontSize:'0.8rem',color: theme_textColorBlended+'B0'}}>Style Name</Typography>} 
                       sx={{ p: 0, mb:1, width: '100%' }}/>

      {/* Style description */}

      <CustomTextField variant='standard' size='small' autoComplete='off'
                       value={props.style.description}
                       onChange={OnStyleDescriptionChanged}
                       label={<Typography sx={{fontSize:'0.8rem',color: theme_textColorBlended+'B0'}}>Style Description</Typography>} 
                       sx={{ p: 0, mb:2, width: '100%' }}/>

      {/* Default special rule */}

      <Stack sx={{ width: '100%', alignItems: 'end', mb: 1 }}>
        <Button size='small' variant='text' startIcon={<EngineeringIcon sx={{ color: theme_orange+'A0' }}/>} onClick={(_)=>OnToggleAdvancedMode()}
                sx={{ color: theme_textColorMain+'A0', fontSize: '0.8rem', textTransform: 'none', px: 1, py: 0.1 }}>
          { props.style.show_advanced_mode ? 'Hide Advanced Mode...' : 'Show Advanced Mode...' }
        </Button>
      </Stack>

      {props.style.show_advanced_mode
        ?
          <Accordion expanded={ props.style.active_rule_id === LAYER_STYLE_EDITOR_SPECIAL_DEFAULT_RULE_ID } key={0} disableGutters={true}
                  slotProps={{ transition: { unmountOnExit: true } }}
                  sx={{ my: 0, width: '100%', boxShadow: 0, bgcolor: 'transparent', p: 0 }} 
                  onChange={(e,expanded) => OnAccordionExpandChange(e,expanded,props.style.default_rule)}>

            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ opacity: 1, height: '1rem', p: 0 }} />}
                              sx={{ width: '100%', p: 0, bgcolor: '#505050'+'90', borderRadius: 20, my: '4px', height: '20px' }}>
              <Stack sx={{ mx: 1, width: '100%' }}>

                <Stack direction='row' sx={{ width: '100%', mx: 1, my: 0.2, alignItems: 'center', justifyContent: 'space-between' }}>

                  <Stack direction='column' sx={{ width: '100%', alignItems: 'start' }}>
                    <Stack direction='row' sx={{ alignItems: 'center' }}>
                      <Typography sx={{ color: theme_orange+'B0', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Global Defaults
                      </Typography>
                      <Typography sx={{ ml: 1, color: theme_textColorBlended+'70', fontSize: '0.8rem' }}>
                        (optional)
                      </Typography>
                    </Stack>
                    <Typography sx={{ ml: 0, width: '100%', color: theme_textColorMain+'70', fontSize: '0.7rem' }}>
                      Any settings defined here can be automatically inherited by all rules within this style.
                    </Typography>
                  </Stack>

                </Stack>

              </Stack>

            </AccordionSummary>
            
            <AccordionDetails sx={{ bgcolor: 'transparent', p:0 }}>

              <EditLayerStyleRule rule={props.style.default_rule} OnRuleUpdated={OnDefaultSpecialRuleUpdated}
                                  default_rule={props.style.default_rule}
                                  layer_shape_type={props.layer_shape_type}
                                  layer={props.layer} OnExpandRule={OnExpandRule}/>

            </AccordionDetails>

          </Accordion>
        :null
      }

      {props.style.rules.length >= 2
        ?
          <Typography sx={{ mt: 1, mx: 2, fontSize: '0.6rem', color: theme_textColorBlended+'90' }}>
            When there are multiple rules, they are drawn on the map in listed order.  You can re-order rules using the small up/down arrow buttons on the right.
          </Typography>
        :
          <Typography sx={{ mt: 1, mx: 2, fontSize: '0.6rem', color: theme_textColorBlended+'90' }}>
            Most styles can be done using a single rule, but for more complex styles you can create as many rules as you like.
          </Typography>
      }

      {/* Style Rules */}

      {props.style.rules.map(function(rule,rule_index)
      {
        return (

          <Accordion 
                expanded={ rule.id === props.style.active_rule_id } 
                key={rule.id} disableGutters={true}
                slotProps={{ transition: { unmountOnExit: true } }}
                sx={{ my: 0, width: '100%', boxShadow: 0, bgcolor: 'transparent', p: 0 }} 
                onChange={(e,expanded) => OnAccordionExpandChange(e,expanded,rule)}>

              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ opacity: 1, height: '1rem', p: 0 }} />}
                                sx={{ width: '100%', p: 0, bgcolor: '#1E4D73', borderRadius: 20, my: '4px', height: '20px' }}>
                <Stack sx={{ mx: 1, width: '100%' }}>

                  <Stack direction='row' sx={{ width: '100%', mx: 1, my: 0.2, alignItems: 'center', justifyContent: 'space-between' }}>

                    <Checkbox size='medium' checked={rule.enabled}
                              onClick={(e)=>OnRuleCheckBoxClicked(e,rule)}
                              sx=
                              {{ 
                                ml: '-12px', mr: '2px', color: theme_textColorBlended, opacity: 0.8, width: '35px', height: '35px',
                                '&.Mui-checked': { color: theme_textColorBlended } 
                              }} />

                    <Stack direction='row' sx={{ width: '100%', alignItems: 'center' }}>
                      <Typography sx={{ width: '120px', color: theme_textColorBlended, fontWeight: 'bold', fontSize: '0.9rem' }}>
                        {/* Rule {rule_index+1} */}
                        Style Rule
                      </Typography>
                      <Typography sx={{ ml: 0, width: '100%', color: theme_limeGreen+'C0', fontSize: '0.9rem' }}>
                        {rule.description}
                      </Typography>
                    </Stack>

                    {/* Rule Utility Buttons */}

                    <IconButton sx={{ ml: 1, p: 0.3, opacity: 0.7 }} onClick={(e)=>OnConfirmDeleteRule(e,rule)}>
                      <DeleteIcon sx={{ width: '18px', height: '18px', color: theme_textColorBlended }} />
                    </IconButton>

                    <Stack>
                      <IconButton sx={{ p: 0, opacity: rule_index===0 ? 0.2 : 0.8 }} onClick={(e)=>OnMoveRuleUp(e,rule_index)}>
                        <KeyboardArrowUpIcon sx={{ width: '25px', height: '15px', color: theme_textColorBlended }}/>
                      </IconButton>
                      <IconButton sx={{ p: 0, opacity: rule_index >= props.style.rules.length - 1 ? 0.2 : 0.8 }} onClick={(e)=>OnMoveRuleDown(e,rule_index)}>
                        <KeyboardArrowDownIcon sx={{ width: '25px', height: '15px', color: theme_textColorBlended }} />
                      </IconButton>
                    </Stack>

                  </Stack>

                </Stack>

              </AccordionSummary>
              
              <AccordionDetails sx={{ bgcolor: 'transparent', p:0 }}>

                <EditLayerStyleRule rule={rule} OnRuleUpdated={OnRuleUpdated}
                                    default_rule={props.style.default_rule}
                                    layer_shape_type={props.layer_shape_type} 
                                    layer={props.layer} OnExpandRule={OnExpandRule}/>

              </AccordionDetails>

          </Accordion>
        )
      })}
      
      <Stack sx={{ mt: 2, alignItems: 'center', width: '100%' }}>
        <Button variant='outlined' onClick={()=>OnCreateNewRule()}
                sx={{ bgcolor: '#1E4D73'+'A0', textTransform: 'none', borderRadius: 20 }}>
          Add a New Rule to this Style
        </Button>
      </Stack>





      {/* "Delete Rule" Yes/No confirmation dialog */}

      <YesNoDialog open={deleteRuleConfirmation} setOpen={setDeleteRuleConfirmation}
                   onYes={OnDeleteRule}>
        <Stack direction='row' sx={{ alignItems: 'center' }}>
          <DeleteIcon sx={{ mr: 2, color: theme_textColorBlended }} />
          <Box sx={{ color: theme_textColorMain }}>Delete rule</Box>
          {/* <Box sx={{ ml: 1, color: theme_orange, fontWeight: 'bold' }}>{deleteRule?.id}</Box> */}
          <Box sx={{ ml: 1, color: theme_textColorMain }}>?</Box>
        </Stack>
        <Typography sx={{ fontSize: '0.7rem', color: theme_textColorBlended }}>
          {deleteRule?.description}
        </Typography>
      </YesNoDialog>

    </Stack>
  )
}



//-------------------------------------------------------------------------------
// Get a new default style.
//
// NOTE: The "existing" array is used to auto-determine the next id.
// NOTE: The new style is created with one default rule.
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle(existingStyles: ILayerStyle[], layer_shape_type: TLayerStyleShapeType): ILayerStyle
{
  const next_id = existingStyles.length === 0 ? 1 : existingStyles.reduce((prev, current) => (prev.id > current.id) ? prev : current).id + 1;

  const rule1: ILayerStyleRule = GetDefaultLayerStyle_RegularRule([], layer_shape_type);

  const newStyle: ILayerStyle = 
  {
    id: next_id,
    name: "",
    description: "",
    default_rule: GetDefaultLayerStyle_DefaultRule(layer_shape_type),
    rules: [ rule1 ],
    show_advanced_mode: false,
    active_rule_id: rule1.id,
  }

  return newStyle;
}